// eslint-disable-next-line no-restricted-imports
import { useRouter } from "next/router";
import { useState } from "react";
import Head from "rivals/components/shared/Head";
import RivalsLink from "rivals/components/shared/Link";
import PageToggle from "rivals/components/shared/PageToggle";
import { TOGGLE_VARIANTS } from "rivals/components/shared/PageToggle/types";
import PageWithTopSection, {
  BODY_BACKGROUNDS
} from "rivals/components/templates/PageWithTopSection";
import { HOST_URL } from "rivals/services/base";
import { PATHS, PRIMARY_SPORTS } from "rivals/shared/constants";
import commonStrings from "rivals/shared/strings/common";
import TeamsGroupingToggle from "./TeamsGroupingToggle";
import TeamsGroupsList from "./TeamsGroupsList";
import TeamsGroupsSelect from "./TeamsGroupsSelect";
import strings from "./strings";
import { GROUPING_TYPES, Props, TEAM_TYPES } from "./types";
import {
  getNextGroupingType,
  getNextSport,
  getNextType,
  setDynamicSegments
} from "./utils";

const { differentTypeLink, mapTeamTypeToString, metaData, title } = strings;
const { sportsToggleTabs } = commonStrings;
const { PageHeader } = PageWithTopSection;
const philter = "teams-page";

const Teams = (props: Props): JSX.Element => {
  const { push } = useRouter();

  const [loading, setLoading] = useState(false);
  const [sport, setSport] = useState<PRIMARY_SPORTS>(props.sport);
  const [type] = useState<TEAM_TYPES>(props.type);
  const [group, setGroup] = useState(props.group);
  const [groupingType, setGroupingType] = useState<GROUPING_TYPES>(
    props.groupingType
  );

  const pageParams = {
    group,
    groupingType,
    sport,
    type
  };

  const onSportChange = (): void => {
    setLoading(true);
    const nextSport = getNextSport(sport);
    const path = setDynamicSegments(
      { ...pageParams, sport: nextSport },
      PATHS.TEAMS
    );
    setSport(nextSport);
    push(path).then(() => setLoading(false));
  };

  const onGroupingTypeChange = (): void => {
    setGroupingType(getNextGroupingType(groupingType, type));
  };

  const onGroupChange = (selectedGroup: string): void => {
    // avoids running this when the selected group equals the current group
    if (selectedGroup !== group) {
      setLoading(true);
      const path = setDynamicSegments(
        { ...pageParams, group: selectedGroup },
        PATHS.TEAMS
      );
      setGroup(selectedGroup);
      push(path).then(() => setLoading(false));
    }
  };

  const getNextTypeLinkPath = (): string => {
    const nextType = getNextType(type);
    const nextSport =
      nextType === TEAM_TYPES.HIGH_SCHOOLS ? PRIMARY_SPORTS.FOOTBALL : sport;
    return setDynamicSegments(
      { ...pageParams, group: "", sport: nextSport, type: nextType },
      PATHS.TEAMS
    );
  };

  return (
    <>
      <Head
        description={metaData.description}
        ogImage=""
        ogUrl={`${HOST_URL}${setDynamicSegments(pageParams, PATHS.TEAMS)}`}
        title={setDynamicSegments(pageParams, metaData.title, true)}
        twitterTitle={setDynamicSegments(pageParams, metaData.title, true)}
      />
      <PageWithTopSection
        bodyBackground={BODY_BACKGROUNDS.WHITE}
        topSection={
          <PageHeader
            headerLink={
              <RivalsLink disabled={loading} href={getNextTypeLinkPath()}>
                {`${differentTypeLink.replace(
                  "[type]",
                  mapTeamTypeToString(getNextType(type))
                )}`}
              </RivalsLink>
            }
            pageTitle={title}
            secondLevelRightSection={
              type === TEAM_TYPES.COLLEGES ? (
                <PageToggle
                  disabled={loading}
                  items={sportsToggleTabs}
                  onToggle={onSportChange}
                  philter={`${philter}-sports-toggle`}
                  selected={sport}
                  variant={TOGGLE_VARIANTS.SPORTS}
                />
              ) : undefined
            }
            subtitle={mapTeamTypeToString(type)}
          />
        }
      >
        <TeamsGroupingToggle
          loading={loading}
          onToggle={onGroupingTypeChange}
          pageParams={pageParams}
          philter={philter}
        />
        <TeamsGroupsSelect
          loading={loading}
          onChange={onGroupChange}
          pageParams={pageParams}
          philter={philter}
        />
        <TeamsGroupsList
          loading={loading}
          pageParams={pageParams}
          philter={philter}
        />
      </PageWithTopSection>
    </>
  );
};

export default Teams;
